import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    FileText,
    Send,
    Plus,
    Trash2,
    Building2,
    Package,
    MapPin,
    Calendar,
    DollarSign,
    Users,
    CheckCircle2,
    Clock,
    AlertCircle,
    Search,
    Filter,
    MoreVertical,
    Eye,
    Edit,
    Copy,
    Ship,
    Plane,
    Truck,
    X,
    ChevronDown,
    Mail,
    Phone,
    Globe
} from 'lucide-react';
import { toast } from 'sonner';

const RFQ = () => {
    const [activeTab, setActiveTab] = useState('create');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showResponsesModal, setShowResponsesModal] = useState(false);
    const [showAddVendorModal, setShowAddVendorModal] = useState(false);
    const [selectedRFQ, setSelectedRFQ] = useState(null);
    const [selectedVendors, setSelectedVendors] = useState([]);
    const [searchVendor, setSearchVendor] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [newVendor, setNewVendor] = useState({
        name: '',
        email: '',
        phone: '',
        specialties: []
    });

    // Form state
    const [rfqForm, setRfqForm] = useState({
        title: '',
        transportMode: 'ocean',
        origin: '',
        destination: '',
        cargoType: '',
        weight: '',
        volume: '',
        quantity: '',
        containerType: '20 STD',
        incoterm: 'FOB',
        requiredDate: '',
        validUntil: '',
        specialRequirements: '',
        hazardous: false,
        insurance: false
    });

    // Sample vendors data
    const vendors = [
        { id: 1, name: 'Maersk Line', email: 'quotes@maersk.com', phone: '+1-800-321-8807', rating: 4.8, specialties: ['Ocean', 'Container'], logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/e/e6/Maersk_Group_Logo.svg/200px-Maersk_Group_Logo.svg.png' },
        { id: 2, name: 'MSC Mediterranean', email: 'rfq@msc.com', phone: '+41-22-703-8888', rating: 4.7, specialties: ['Ocean', 'Bulk'], logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/7/71/MSC_Logo.svg/200px-MSC_Logo.svg.png' },
        { id: 3, name: 'CMA CGM', email: 'sales@cma-cgm.com', phone: '+33-4-88-91-90-00', rating: 4.6, specialties: ['Ocean', 'Reefer'], logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/6/6e/CMA_CGM_logo.svg/200px-CMA_CGM_logo.svg.png' },
        { id: 4, name: 'Hapag-Lloyd', email: 'quotes@hapag-lloyd.com', phone: '+49-40-3001-0', rating: 4.9, specialties: ['Ocean', 'Premium'], logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/a/a8/Hapag-Lloyd_Logo.svg/200px-Hapag-Lloyd_Logo.svg.png' },
        { id: 5, name: 'Evergreen Marine', email: 'rfq@evergreen-marine.com', phone: '+886-2-2505-7766', rating: 4.5, specialties: ['Ocean', 'Asia Routes'], logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/4/4b/Evergreen_Marine_Corporation_Logo.svg/200px-Evergreen_Marine_Corporation_Logo.svg.png' },
        { id: 6, name: 'COSCO Shipping', email: 'sales@cosco.com', phone: '+86-21-6596-6105', rating: 4.4, specialties: ['Ocean', 'China Routes'], logo: 'https://upload.wikimedia.org/wikipedia/en/thumb/d/d7/COSCO_logo.svg/200px-COSCO_logo.svg.png' },
        { id: 7, name: 'DHL Global Forwarding', email: 'quotes@dhl.com', phone: '+49-228-182-0', rating: 4.7, specialties: ['Air', 'Express'], logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/a/ac/DHL_Logo.svg/200px-DHL_Logo.svg.png' },
        { id: 8, name: 'Kuehne + Nagel', email: 'rfq@kuehne-nagel.com', phone: '+41-44-786-95-11', rating: 4.8, specialties: ['Ocean', 'Air', 'Road'], logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/4/41/Kuehne%2BNagel_logo.svg/200px-Kuehne%2BNagel_logo.svg.png' },
    ];

    // Sample RFQ history
    const [rfqHistory] = useState([
        { id: 'RFQ-2024-001', title: 'Shanghai to Los Angeles - 40HC', status: 'responded', vendors: 5, responses: 4, bestRate: '$2,850', created: '2024-12-20', deadline: '2024-12-25' },
        { id: 'RFQ-2024-002', title: 'Rotterdam to New York - 20GP', status: 'pending', vendors: 3, responses: 1, bestRate: '$1,950', created: '2024-12-21', deadline: '2024-12-26' },
        { id: 'RFQ-2024-003', title: 'Singapore to Hamburg - Reefer', status: 'closed', vendors: 6, responses: 6, bestRate: '$4,200', created: '2024-12-15', deadline: '2024-12-20' },
        { id: 'RFQ-2024-004', title: 'Tokyo to Long Beach - 40HC', status: 'draft', vendors: 0, responses: 0, bestRate: '-', created: '2024-12-22', deadline: '-' },
        { id: 'RFQ-2024-005', title: 'Mumbai to Felixstowe - Break Bulk', status: 'responded', vendors: 4, responses: 3, bestRate: '$3,100', created: '2024-12-18', deadline: '2024-12-23' },
    ]);

    // Vendor responses for each RFQ
    const vendorResponses = {
        'RFQ-2024-001': [
            { vendor: 'Maersk Line', rate: '$2,850', transitDays: 18, validUntil: '2024-12-30', status: 'accepted', notes: 'Best rate, direct service', freightCharges: '$2,600', surcharges: '$250' },
            { vendor: 'MSC Mediterranean', rate: '$2,920', transitDays: 20, validUntil: '2024-12-28', status: 'received', notes: 'Transshipment via Busan', freightCharges: '$2,650', surcharges: '$270' },
            { vendor: 'CMA CGM', rate: '$3,100', transitDays: 17, validUntil: '2024-12-29', status: 'received', notes: 'Premium express service', freightCharges: '$2,800', surcharges: '$300' },
            { vendor: 'Hapag-Lloyd', rate: '$2,950', transitDays: 19, validUntil: '2024-12-31', status: 'received', notes: 'Direct vessel, reliable schedule', freightCharges: '$2,700', surcharges: '$250' },
            { vendor: 'Evergreen Marine', rate: '-', transitDays: '-', validUntil: '-', status: 'pending', notes: 'Awaiting response', freightCharges: '-', surcharges: '-' },
        ],
        'RFQ-2024-002': [
            { vendor: 'MSC Mediterranean', rate: '$1,950', transitDays: 12, validUntil: '2024-12-30', status: 'received', notes: 'Weekly service available', freightCharges: '$1,750', surcharges: '$200' },
            { vendor: 'Hapag-Lloyd', rate: '-', transitDays: '-', validUntil: '-', status: 'pending', notes: 'Awaiting response', freightCharges: '-', surcharges: '-' },
            { vendor: 'CMA CGM', rate: '-', transitDays: '-', validUntil: '-', status: 'pending', notes: 'Awaiting response', freightCharges: '-', surcharges: '-' },
        ],
        'RFQ-2024-003': [
            { vendor: 'Maersk Line', rate: '$4,500', transitDays: 25, validUntil: '2024-12-25', status: 'rejected', notes: 'Rate too high', freightCharges: '$4,000', surcharges: '$500' },
            { vendor: 'MSC Mediterranean', rate: '$4,350', transitDays: 26, validUntil: '2024-12-24', status: 'received', notes: 'Reefer equipment available', freightCharges: '$3,900', surcharges: '$450' },
            { vendor: 'CMA CGM', rate: '$4,200', transitDays: 24, validUntil: '2024-12-25', status: 'accepted', notes: 'Best reefer service', freightCharges: '$3,750', surcharges: '$450' },
            { vendor: 'Hapag-Lloyd', rate: '$4,400', transitDays: 23, validUntil: '2024-12-23', status: 'received', notes: 'Fast transit, premium reefer', freightCharges: '$3,950', surcharges: '$450' },
            { vendor: 'Evergreen Marine', rate: '$4,600', transitDays: 27, validUntil: '2024-12-22', status: 'rejected', notes: 'Transit time too long', freightCharges: '$4,100', surcharges: '$500' },
            { vendor: 'COSCO Shipping', rate: '$4,250', transitDays: 25, validUntil: '2024-12-24', status: 'received', notes: 'Good alternative option', freightCharges: '$3,800', surcharges: '$450' },
        ],
        'RFQ-2024-004': [],
        'RFQ-2024-005': [
            { vendor: 'Kuehne + Nagel', rate: '$3,100', transitDays: 22, validUntil: '2024-12-28', status: 'received', notes: 'Break bulk specialist', freightCharges: '$2,800', surcharges: '$300' },
            { vendor: 'DHL Global Forwarding', rate: '$3,250', transitDays: 20, validUntil: '2024-12-27', status: 'received', notes: 'Fast handling', freightCharges: '$2,950', surcharges: '$300' },
            { vendor: 'MSC Mediterranean', rate: '$3,400', transitDays: 24, validUntil: '2024-12-26', status: 'received', notes: 'Standard break bulk service', freightCharges: '$3,100', surcharges: '$300' },
            { vendor: 'Maersk Line', rate: '-', transitDays: '-', validUntil: '-', status: 'pending', notes: 'Awaiting response', freightCharges: '-', surcharges: '-' },
        ],
    };

    const handleViewResponses = (rfq) => {
        setSelectedRFQ(rfq);
        setShowResponsesModal(true);
    };

    const getResponseStatusBadge = (status) => {
        const styles = {
            accepted: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
            received: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
            pending: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
            rejected: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
        };
        return (
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${styles[status]}`}>
                {status.charAt(0).toUpperCase() + status.slice(1)}
            </span>
        );
    };

    const containerTypes = ['20 STD', '40 STD', '40HC', '20RF', '40RF', '45HC', 'Open Top', 'Flat Rack'];
    const incoterms = ['EXW', 'FCA', 'CPT', 'CIP', 'DAP', 'DPU', 'DDP', 'FAS', 'FOB', 'CFR', 'CIF'];

    const toggleVendor = (vendor) => {
        if (selectedVendors.find(v => v.id === vendor.id)) {
            setSelectedVendors(selectedVendors.filter(v => v.id !== vendor.id));
        } else {
            setSelectedVendors([...selectedVendors, vendor]);
        }
    };

    const selectAllVendors = () => {
        if (selectedVendors.length === vendors.length) {
            setSelectedVendors([]);
        } else {
            setSelectedVendors([...vendors]);
        }
    };

    const handleSubmitRFQ = () => {
        if (selectedVendors.length === 0) {
            toast.error('Please select at least one vendor');
            return;
        }
        if (!rfqForm.title || !rfqForm.origin || !rfqForm.destination) {
            toast.error('Please fill in all required fields');
            return;
        }

        toast.success(`RFQ sent to ${selectedVendors.length} vendors successfully!`);
        setShowCreateModal(false);
        setSelectedVendors([]);
        setRfqForm({
            title: '',
            transportMode: 'ocean',
            origin: '',
            destination: '',
            cargoType: '',
            weight: '',
            volume: '',
            quantity: '',
            containerType: '20 STD',
            incoterm: 'FOB',
            requiredDate: '',
            validUntil: '',
            specialRequirements: '',
            hazardous: false,
            insurance: false
        });
    };

    const getStatusBadge = (status) => {
        const styles = {
            responded: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
            pending: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
            closed: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300',
            draft: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
        };
        const icons = {
            responded: <CheckCircle2 className="w-3 h-3" />,
            pending: <Clock className="w-3 h-3" />,
            closed: <AlertCircle className="w-3 h-3" />,
            draft: <Edit className="w-3 h-3" />
        };
        return (
            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${styles[status]}`}>
                {icons[status]}
                {status.charAt(0).toUpperCase() + status.slice(1)}
            </span>
        );
    };

    const filteredVendors = vendors.filter(v =>
        v.name.toLowerCase().includes(searchVendor.toLowerCase()) ||
        v.specialties.some(s => s.toLowerCase().includes(searchVendor.toLowerCase()))
    );

    const filteredHistory = rfqHistory.filter(rfq =>
        filterStatus === 'all' || rfq.status === filterStatus
    );

    return (
        <div className="p-6 space-y-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                        <FileText className="w-7 h-7 text-[#0072FF]" />
                        Request for Quotation (RFQ)
                    </h1>
                    <p className="text-slate-600 dark:text-slate-400 mt-1">
                        Send rate requests to multiple vendors simultaneously
                    </p>
                </div>
                <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setShowCreateModal(true)}
                    className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#0072FF] to-[#00C6FB] text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all"
                >
                    <Plus className="w-5 h-5" />
                    Create New RFQ
                </motion.button>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {[
                    { label: 'Total RFQs', value: '24', icon: FileText, color: 'from-blue-500 to-blue-600', bgColor: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800' },
                    { label: 'Pending Responses', value: '8', icon: Clock, color: 'from-yellow-500 to-orange-500', bgColor: 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800' },
                    { label: 'Quotes Received', value: '45', icon: CheckCircle2, color: 'from-green-500 to-emerald-500', bgColor: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' },
                    { label: 'Active Vendors', value: '12', icon: Building2, color: 'from-purple-500 to-pink-500', bgColor: 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800' }
                ].map((stat, index) => (
                    <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className={`rounded-xl p-5 border shadow-sm ${stat.bgColor}`}
                    >
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-slate-600 dark:text-slate-400">{stat.label}</p>
                                <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1">{stat.value}</p>
                            </div>
                            <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center`}>
                                <stat.icon className="w-6 h-6 text-white" />
                            </div>
                        </div>
                    </motion.div>
                ))}
            </div>

            {/* Tabs */}
            <div className="flex gap-2 border-b border-slate-200 dark:border-slate-700">
                {['create', 'history', 'vendors'].map((tab) => (
                    <button
                        key={tab}
                        onClick={() => setActiveTab(tab)}
                        className={`px-4 py-3 font-medium text-sm transition-all border-b-2 ${activeTab === tab
                                ? 'text-[#0072FF] border-[#0072FF]'
                                : 'text-slate-600 dark:text-slate-400 border-transparent hover:text-slate-900 dark:hover:text-white'
                            }`}
                    >
                        {tab === 'create' && 'Quick RFQ'}
                        {tab === 'history' && 'RFQ History'}
                        {tab === 'vendors' && 'Vendor Directory'}
                    </button>
                ))}
            </div>

            {/* Tab Content */}
            <AnimatePresence mode="wait">
                {activeTab === 'create' && (
                    <motion.div
                        key="create"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        className="grid grid-cols-1 lg:grid-cols-3 gap-6"
                    >
                        {/* Quick RFQ Form */}
                        <div className="lg:col-span-2 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
                            <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">Quick RFQ Details</h3>

                            <div className="grid grid-cols-1 md:grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-red-600 dark:text-red-400 mb-1">RFQ Title *</label>
                                    <input
                                        type="text"
                                        value={rfqForm.title}
                                        onChange={(e) => setRfqForm({ ...rfqForm, title: e.target.value })}
                                        placeholder="e.g., Shanghai to Los Angeles - 40HC Container"
                                        className="w-full px-4 py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-[#0072FF] focus:border-transparent"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Transport Mode</label>
                                    <div className="flex gap-2">
                                        {[
                                            { mode: 'ocean', icon: Ship, label: 'Ocean' },
                                            { mode: 'air', icon: Plane, label: 'Air' },
                                            { mode: 'road', icon: Truck, label: 'Road' }
                                        ].map(({ mode, icon: Icon, label }) => (
                                            <button
                                                key={mode}
                                                onClick={() => setRfqForm({ ...rfqForm, transportMode: mode })}
                                                className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border transition-all ${rfqForm.transportMode === mode
                                                        ? 'border-[#0072FF] bg-[#0072FF]/10 text-[#0072FF]'
                                                        : 'border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-400 hover:border-slate-400'
                                                    }`}
                                            >
                                                <Icon className="w-4 h-4" />
                                                <span className="text-sm font-medium">{label}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Container Type</label>
                                    <select
                                        value={rfqForm.containerType}
                                        onChange={(e) => setRfqForm({ ...rfqForm, containerType: e.target.value })}
                                        className="w-full px-4 py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-[#0072FF]"
                                    >
                                        {containerTypes.map(type => (
                                            <option key={type} value={type}>{type}</option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-red-600 dark:text-red-400 mb-1">Origin *</label>
                                    <div className="relative">
                                        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                        <input
                                            type="text"
                                            value={rfqForm.origin}
                                            onChange={(e) => setRfqForm({ ...rfqForm, origin: e.target.value })}
                                            placeholder="Port/City of origin"
                                            className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-[#0072FF]"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-red-600 dark:text-red-400 mb-1">Destination *</label>
                                    <div className="relative">
                                        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                        <input
                                            type="text"
                                            value={rfqForm.destination}
                                            onChange={(e) => setRfqForm({ ...rfqForm, destination: e.target.value })}
                                            placeholder="Port/City of destination"
                                            className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-[#0072FF]"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Cargo Type</label>
                                    <input
                                        type="text"
                                        value={rfqForm.cargoType}
                                        onChange={(e) => setRfqForm({ ...rfqForm, cargoType: e.target.value })}
                                        placeholder="e.g., Electronics, Textiles"
                                        className="w-full px-4 py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-[#0072FF]"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Incoterm</label>
                                    <select
                                        value={rfqForm.incoterm}
                                        onChange={(e) => setRfqForm({ ...rfqForm, incoterm: e.target.value })}
                                        className="w-full px-4 py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-[#0072FF]"
                                    >
                                        {incoterms.map(term => (
                                            <option key={term} value={term}>{term}</option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Weight (KG)</label>
                                    <input
                                        type="number"
                                        value={rfqForm.weight}
                                        onChange={(e) => setRfqForm({ ...rfqForm, weight: e.target.value })}
                                        placeholder="Total weight"
                                        className="w-full px-4 py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-[#0072FF]"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Volume (CBM)</label>
                                    <input
                                        type="number"
                                        value={rfqForm.volume}
                                        onChange={(e) => setRfqForm({ ...rfqForm, volume: e.target.value })}
                                        placeholder="Total volume"
                                        className="w-full px-4 py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-[#0072FF]"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Required Date</label>
                                    <div className="relative">
                                        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                        <input
                                            type="date"
                                            value={rfqForm.requiredDate}
                                            onChange={(e) => setRfqForm({ ...rfqForm, requiredDate: e.target.value })}
                                            className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-[#0072FF]"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Quote Valid Until</label>
                                    <div className="relative">
                                        <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                        <input
                                            type="date"
                                            value={rfqForm.validUntil}
                                            onChange={(e) => setRfqForm({ ...rfqForm, validUntil: e.target.value })}
                                            className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-[#0072FF]"
                                        />
                                    </div>
                                </div>

                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Special Requirements</label>
                                    <textarea
                                        value={rfqForm.specialRequirements}
                                        onChange={(e) => setRfqForm({ ...rfqForm, specialRequirements: e.target.value })}
                                        placeholder="Any special handling, temperature requirements, etc."
                                        rows={3}
                                        className="w-full px-4 py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-[#0072FF]"
                                    />
                                </div>

                                <div className="md:col-span-2 flex gap-6">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={rfqForm.hazardous}
                                            onChange={(e) => setRfqForm({ ...rfqForm, hazardous: e.target.checked })}
                                            className="w-4 h-4 rounded border-slate-300 text-[#0072FF] focus:ring-[#0072FF]"
                                        />
                                        <span className="text-sm text-slate-700 dark:text-slate-300">Hazardous Goods</span>
                                    </label>
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={rfqForm.insurance}
                                            onChange={(e) => setRfqForm({ ...rfqForm, insurance: e.target.checked })}
                                            className="w-4 h-4 rounded border-slate-300 text-[#0072FF] focus:ring-[#0072FF]"
                                        />
                                        <span className="text-sm text-slate-700 dark:text-slate-300">Include Insurance Quote</span>
                                    </label>
                                </div>
                            </div>
                        </div>

                        {/* Vendor Selection */}
                        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-6">
                            <div className="flex items-center justify-between mb-4">
                                <div className="flex items-center gap-2">
                                    <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Select Vendors</h3>
                                    <button
                                        onClick={() => setShowAddVendorModal(true)}
                                        className="w-7 h-7 flex items-center justify-center rounded-full bg-gradient-to-r from-[#0072FF] to-[#00C6FB] text-white hover:shadow-lg transition-all"
                                        title="Add New Vendor"
                                    >
                                        <Plus className="w-4 h-4" />
                                    </button>
                                </div>
                                <button
                                    onClick={selectAllVendors}
                                    className="text-sm text-[#0072FF] hover:underline"
                                >
                                    {selectedVendors.length === vendors.length ? 'Deselect All' : 'Select All'}
                                </button>
                            </div>

                            <div className="relative mb-4">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                <input
                                    type="text"
                                    value={searchVendor}
                                    onChange={(e) => setSearchVendor(e.target.value)}
                                    placeholder="Search vendors..."
                                    className="w-full pl-10 pr-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white text-sm"
                                />
                            </div>

                            <div className="space-y-2 max-h-[400px] overflow-y-auto">
                                {filteredVendors.map((vendor) => (
                                    <motion.div
                                        key={vendor.id}
                                        whileHover={{ scale: 1.01 }}
                                        onClick={() => toggleVendor(vendor)}
                                        className={`p-3 rounded-lg border cursor-pointer transition-all ${selectedVendors.find(v => v.id === vendor.id)
                                                ? 'border-[#0072FF] bg-[#0072FF]/5'
                                                : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'
                                            }`}
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${selectedVendors.find(v => v.id === vendor.id)
                                                    ? 'border-[#0072FF] bg-[#0072FF]'
                                                    : 'border-slate-300'
                                                }`}>
                                                {selectedVendors.find(v => v.id === vendor.id) && (
                                                    <CheckCircle2 className="w-3 h-3 text-white" />
                                                )}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="font-medium text-sm text-slate-900 dark:text-white truncate">{vendor.name}</p>
                                                <div className="flex items-center gap-2 mt-1">
                                                    {vendor.specialties.slice(0, 2).map((s, i) => (
                                                        <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400">
                                                            {s}
                                                        </span>
                                                    ))}
                                                    <span className="text-xs text-yellow-500">★ {vendor.rating}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>

                            {selectedVendors.length > 0 && (
                                <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-700">
                                    <p className="text-sm text-slate-600 dark:text-slate-400 mb-3">
                                        <span className="font-semibold text-[#0072FF]">{selectedVendors.length}</span> vendor(s) selected
                                    </p>
                                    <motion.button
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                        onClick={handleSubmitRFQ}
                                        className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-[#0072FF] to-[#00C6FB] text-white rounded-xl font-semibold shadow-lg"
                                    >
                                        <Send className="w-4 h-4" />
                                        Send RFQ to {selectedVendors.length} Vendors
                                    </motion.button>
                                </div>
                            )}
                        </div>
                    </motion.div>
                )}

                {activeTab === 'history' && (
                    <motion.div
                        key="history"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                    >
                        {/* Filters */}
                        <div className="flex items-center gap-4 mb-4">
                            <div className="relative flex-1 max-w-md">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                <input
                                    type="text"
                                    placeholder="Search RFQs..."
                                    className="w-full pl-10 pr-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white text-sm"
                                />
                            </div>
                            <select
                                value={filterStatus}
                                onChange={(e) => setFilterStatus(e.target.value)}
                                className="px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white text-sm"
                            >
                                <option value="all">All Status</option>
                                <option value="responded">Responded</option>
                                <option value="pending">Pending</option>
                                <option value="closed">Closed</option>
                                <option value="draft">Draft</option>
                            </select>
                        </div>

                        {/* RFQ Table */}
                        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead>
                                        <tr className="bg-slate-50 dark:bg-slate-900/50">
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase">RFQ ID</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase">Title</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase">Status</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase">Vendors</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase">Responses</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase">Best Rate</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase">Deadline</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                                        {filteredHistory.map((rfq) => (
                                            <tr key={rfq.id} className="hover:bg-slate-50 dark:hover:bg-slate-900/30 transition-colors">
                                                <td className="px-4 py-3">
                                                    <span className="font-mono text-sm text-[#0072FF]">{rfq.id}</span>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <span className="text-sm font-medium text-slate-900 dark:text-white">{rfq.title}</span>
                                                </td>
                                                <td className="px-4 py-3">
                                                    {getStatusBadge(rfq.status)}
                                                </td>
                                                <td className="px-4 py-3">
                                                    <span className="text-sm text-slate-600 dark:text-slate-400">{rfq.vendors}</span>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <span className="text-sm">
                                                        <span className="font-semibold text-green-600 dark:text-green-400">{rfq.responses}</span>
                                                        <span className="text-slate-400 dark:text-slate-500">/</span>
                                                        <span className="font-semibold text-blue-600 dark:text-blue-400">{rfq.vendors}</span>
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <span className="text-sm font-semibold text-green-600 dark:text-green-400">{rfq.bestRate}</span>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <span className="text-sm text-slate-600 dark:text-slate-400">{rfq.deadline}</span>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <div className="flex items-center gap-2">
                                                        <button
                                                            onClick={() => handleViewResponses(rfq)}
                                                            className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-400"
                                                            title="View Responses"
                                                        >
                                                            <Eye className="w-4 h-4" />
                                                        </button>
                                                        <button className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-400">
                                                            <Copy className="w-4 h-4" />
                                                        </button>
                                                        <button className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-400">
                                                            <MoreVertical className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </motion.div>
                )}

                {activeTab === 'vendors' && (
                    <motion.div
                        key="vendors"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        className="grid grid-cols-1 md:grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
                    >
                        {vendors.map((vendor) => (
                            <motion.div
                                key={vendor.id}
                                whileHover={{ y: -4 }}
                                className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-5 hover:shadow-lg transition-all"
                            >
                                <div className="flex items-start justify-between mb-4">
                                    <div className="flex items-center gap-3">
                                        <div className="w-12 h-12 rounded-lg bg-slate-100 dark:bg-slate-700 flex items-center justify-center">
                                            <Building2 className="w-6 h-6 text-slate-600 dark:text-slate-400" />
                                        </div>
                                        <div>
                                            <h3 className="font-semibold text-slate-900 dark:text-white">{vendor.name}</h3>
                                            <div className="flex items-center gap-1 mt-0.5">
                                                <span className="text-yellow-500">★</span>
                                                <span className="text-sm text-slate-600 dark:text-slate-400">{vendor.rating}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-2 mb-4">
                                    <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                                        <Mail className="w-4 h-4" />
                                        <span>{vendor.email}</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                                        <Phone className="w-4 h-4" />
                                        <span>{vendor.phone}</span>
                                    </div>
                                </div>

                                <div className="flex flex-wrap gap-2 mb-4">
                                    {vendor.specialties.map((s, i) => (
                                        <span key={i} className="text-xs px-2 py-1 rounded-full bg-[#0072FF]/10 text-[#0072FF]">
                                            {s}
                                        </span>
                                    ))}
                                </div>

                                <button className="w-full py-2 text-sm font-medium text-[#0072FF] border border-[#0072FF] rounded-lg hover:bg-[#0072FF]/5 transition-colors">
                                    Send RFQ
                                </button>
                            </motion.div>
                        ))}
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Create RFQ Modal */}
            <AnimatePresence>
                {showCreateModal && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
                        onClick={() => setShowCreateModal(false)}
                    >
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.95, opacity: 0 }}
                            onClick={(e) => e.stopPropagation()}
                            className="bg-white dark:bg-slate-800 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
                        >
                            <div className="sticky top-0 bg-white dark:bg-slate-800 px-6 py-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
                                <h2 className="text-xl font-bold text-slate-900 dark:text-white">Create New RFQ</h2>
                                <button
                                    onClick={() => setShowCreateModal(false)}
                                    className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700"
                                >
                                    <X className="w-5 h-5 text-slate-500" />
                                </button>
                            </div>

                            <div className="p-6">
                                <p className="text-slate-600 dark:text-slate-400 mb-6">
                                    Fill in the details below and select vendors to send your RFQ request.
                                </p>

                                {/* Modal content - reusing Quick RFQ form */}
                                <div className="grid grid-cols-1 md:grid-cols-1 sm:grid-cols-2 gap-4">
                                    {/* Same form fields as Quick RFQ */}
                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-medium text-red-600 dark:text-red-400 mb-1">RFQ Title *</label>
                                        <input
                                            type="text"
                                            value={rfqForm.title}
                                            onChange={(e) => setRfqForm({ ...rfqForm, title: e.target.value })}
                                            placeholder="e.g., Shanghai to Los Angeles - 40HC Container"
                                            className="w-full px-4 py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-red-600 dark:text-red-400 mb-1">Origin *</label>
                                        <input
                                            type="text"
                                            value={rfqForm.origin}
                                            onChange={(e) => setRfqForm({ ...rfqForm, origin: e.target.value })}
                                            placeholder="Port/City of origin"
                                            className="w-full px-4 py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-red-600 dark:text-red-400 mb-1">Destination *</label>
                                        <input
                                            type="text"
                                            value={rfqForm.destination}
                                            onChange={(e) => setRfqForm({ ...rfqForm, destination: e.target.value })}
                                            placeholder="Port/City of destination"
                                            className="w-full px-4 py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                                        />
                                    </div>
                                </div>

                                {/* Vendor Selection in Modal */}
                                <div className="mt-6">
                                    <div className="flex items-center justify-between mb-3">
                                        <label className="block text-sm font-medium text-red-600 dark:text-red-400">Select Vendors *</label>
                                        <button
                                            onClick={selectAllVendors}
                                            className="text-sm text-[#0072FF] hover:underline"
                                        >
                                            {selectedVendors.length === vendors.length ? 'Deselect All' : 'Select All'}
                                        </button>
                                    </div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2">
                                        {vendors.map((vendor) => (
                                            <div
                                                key={vendor.id}
                                                onClick={() => toggleVendor(vendor)}
                                                className={`p-3 rounded-lg border cursor-pointer transition-all ${selectedVendors.find(v => v.id === vendor.id)
                                                        ? 'border-[#0072FF] bg-[#0072FF]/5'
                                                        : 'border-slate-200 dark:border-slate-700 hover:border-slate-300'
                                                    }`}
                                            >
                                                <div className="flex items-center gap-2">
                                                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${selectedVendors.find(v => v.id === vendor.id)
                                                            ? 'border-[#0072FF] bg-[#0072FF]'
                                                            : 'border-slate-300'
                                                        }`}>
                                                        {selectedVendors.find(v => v.id === vendor.id) && (
                                                            <CheckCircle2 className="w-2.5 h-2.5 text-white" />
                                                        )}
                                                    </div>
                                                    <span className="text-sm font-medium text-slate-900 dark:text-white truncate">{vendor.name}</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Submit Button */}
                                <div className="mt-6 flex justify-end gap-3">
                                    <button
                                        onClick={() => setShowCreateModal(false)}
                                        className="px-6 py-2.5 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg font-medium"
                                    >
                                        Cancel
                                    </button>
                                    <motion.button
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                        onClick={handleSubmitRFQ}
                                        disabled={selectedVendors.length === 0}
                                        className="px-6 py-2.5 bg-gradient-to-r from-[#0072FF] to-[#00C6FB] text-white rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                    >
                                        <Send className="w-4 h-4" />
                                        Send RFQ ({selectedVendors.length})
                                    </motion.button>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Vendor Responses Modal */}
            <AnimatePresence>
                {showResponsesModal && selectedRFQ && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
                        onClick={() => setShowResponsesModal(false)}
                    >
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.95, opacity: 0 }}
                            onClick={(e) => e.stopPropagation()}
                            className="bg-white dark:bg-slate-800 rounded-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col"
                        >
                            {/* Modal Header */}
                            <div className="sticky top-0 bg-white dark:bg-slate-800 px-6 py-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
                                <div>
                                    <h2 className="text-xl font-bold text-slate-900 dark:text-white">Vendor Responses</h2>
                                    <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                                        {selectedRFQ.id} - {selectedRFQ.title}
                                    </p>
                                </div>
                                <button
                                    onClick={() => setShowResponsesModal(false)}
                                    className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700"
                                >
                                    <X className="w-5 h-5 text-slate-500" />
                                </button>
                            </div>

                            {/* RFQ Summary */}
                            <div className="px-6 py-4 bg-slate-50 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-700">
                                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4">
                                    <div>
                                        <p className="text-xs text-slate-500 dark:text-slate-400">Status</p>
                                        <div className="mt-1">{getStatusBadge(selectedRFQ.status)}</div>
                                    </div>
                                    <div>
                                        <p className="text-xs text-slate-500 dark:text-slate-400">Vendors Contacted</p>
                                        <p className="text-lg font-semibold text-slate-900 dark:text-white">{selectedRFQ.vendors}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-slate-500 dark:text-slate-400">Responses Received</p>
                                        <p className="text-lg font-semibold text-slate-900 dark:text-white">{selectedRFQ.responses}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-slate-500 dark:text-slate-400">Best Rate</p>
                                        <p className="text-lg font-semibold text-green-600 dark:text-green-400">{selectedRFQ.bestRate}</p>
                                    </div>
                                    <div>
                                        <p className="text-xs text-slate-500 dark:text-slate-400">Deadline</p>
                                        <p className="text-lg font-semibold text-slate-900 dark:text-white">{selectedRFQ.deadline}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Responses Table */}
                            <div className="flex-1 overflow-auto p-6">
                                {vendorResponses[selectedRFQ.id]?.length > 0 ? (
                                    <div className="overflow-x-auto">
                                        <table className="w-full">
                                            <thead>
                                                <tr className="bg-slate-50 dark:bg-slate-900/50">
                                                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase">Vendor</th>
                                                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase">Rate</th>
                                                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase">Freight</th>
                                                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase">Surcharges</th>
                                                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase">Transit</th>
                                                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase">Valid Until</th>
                                                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase">Status</th>
                                                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase">Notes</th>
                                                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase">Action</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                                                {vendorResponses[selectedRFQ.id].map((response, index) => (
                                                    <tr key={index} className="hover:bg-slate-50 dark:hover:bg-slate-900/30 transition-colors">
                                                        <td className="px-4 py-3">
                                                            <div className="flex items-center gap-2">
                                                                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#0072FF] to-[#00C6FB] flex items-center justify-center text-white text-xs font-bold">
                                                                    {response.vendor.charAt(0)}
                                                                </div>
                                                                <span className="text-sm font-medium text-slate-900 dark:text-white">{response.vendor}</span>
                                                            </div>
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            <span className={`text-sm font-bold ${response.rate === selectedRFQ.bestRate ? 'text-green-600 dark:text-green-400' : 'text-slate-900 dark:text-white'}`}>
                                                                {response.rate}
                                                            </span>
                                                            {response.rate === selectedRFQ.bestRate && response.rate !== '-' && (
                                                                <span className="ml-2 text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 px-2 py-0.5 rounded-full">Best</span>
                                                            )}
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            <span className="text-sm text-slate-600 dark:text-slate-400">{response.freightCharges}</span>
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            <span className="text-sm text-slate-600 dark:text-slate-400">{response.surcharges}</span>
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            <span className="text-sm text-slate-600 dark:text-slate-400">
                                                                {response.transitDays !== '-' ? `${response.transitDays} days` : '-'}
                                                            </span>
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            <span className="text-sm text-slate-600 dark:text-slate-400">{response.validUntil}</span>
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            {getResponseStatusBadge(response.status)}
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            <span className="text-sm text-slate-500 dark:text-slate-400 max-w-[150px] truncate block" title={response.notes}>
                                                                {response.notes}
                                                            </span>
                                                        </td>
                                                        <td className="px-4 py-3">
                                                            {response.status === 'received' && (
                                                                <div className="flex items-center gap-1">
                                                                    <button className="px-3 py-1.5 text-xs font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors">
                                                                        Accept
                                                                    </button>
                                                                    <button className="px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors">
                                                                        Reject
                                                                    </button>
                                                                </div>
                                                            )}
                                                            {response.status === 'accepted' && (
                                                                <span className="text-xs text-green-600 font-medium">✓ Accepted</span>
                                                            )}
                                                            {response.status === 'rejected' && (
                                                                <span className="text-xs text-red-600 font-medium">✗ Rejected</span>
                                                            )}
                                                            {response.status === 'pending' && (
                                                                <span className="text-xs text-yellow-600 font-medium">Awaiting...</span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <div className="text-center py-12">
                                        <div className="w-16 h-16 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center mx-auto mb-4">
                                            <FileText className="w-8 h-8 text-slate-400" />
                                        </div>
                                        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">No Responses Yet</h3>
                                        <p className="text-sm text-slate-500 dark:text-slate-400">
                                            This RFQ is still in draft mode. Send it to vendors to receive responses.
                                        </p>
                                    </div>
                                )}
                            </div>

                            {/* Modal Footer */}
                            <div className="sticky bottom-0 bg-white dark:bg-slate-800 px-6 py-4 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-3">
                                <button
                                    onClick={() => setShowResponsesModal(false)}
                                    className="px-6 py-2.5 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg font-medium"
                                >
                                    Close
                                </button>
                                <button className="px-6 py-2.5 bg-gradient-to-r from-[#0072FF] to-[#00C6FB] text-white rounded-lg font-semibold flex items-center gap-2">
                                    <Send className="w-4 h-4" />
                                    Export Report
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Add New Vendor Modal */}
            <AnimatePresence>
                {showAddVendorModal && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
                        onClick={() => setShowAddVendorModal(false)}
                    >
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.95, opacity: 0 }}
                            onClick={(e) => e.stopPropagation()}
                            className="bg-white dark:bg-slate-800 rounded-2xl max-w-md w-full overflow-hidden"
                        >
                            {/* Modal Header */}
                            <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-gradient-to-r from-[#0072FF] to-[#00C6FB] flex items-center justify-center">
                                        <Building2 className="w-5 h-5 text-white" />
                                    </div>
                                    <h2 className="text-xl font-bold text-slate-900 dark:text-white">Add New Vendor</h2>
                                </div>
                                <button
                                    onClick={() => setShowAddVendorModal(false)}
                                    className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700"
                                >
                                    <X className="w-5 h-5 text-slate-500" />
                                </button>
                            </div>

                            {/* Modal Content */}
                            <div className="p-6 space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-red-600 dark:text-red-400 mb-1">Vendor Name *</label>
                                    <input
                                        type="text"
                                        value={newVendor.name}
                                        onChange={(e) => setNewVendor({ ...newVendor, name: e.target.value })}
                                        placeholder="e.g., Pacific Shipping Co."
                                        className="w-full px-4 py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-[#0072FF]"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-red-600 dark:text-red-400 mb-1">Email Address *</label>
                                    <div className="relative">
                                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                        <input
                                            type="email"
                                            value={newVendor.email}
                                            onChange={(e) => setNewVendor({ ...newVendor, email: e.target.value })}
                                            placeholder="rfq@company.com"
                                            className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-[#0072FF]"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Phone Number</label>
                                    <div className="relative">
                                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                        <input
                                            type="tel"
                                            value={newVendor.phone}
                                            onChange={(e) => setNewVendor({ ...newVendor, phone: e.target.value })}
                                            placeholder="+1-800-123-4567"
                                            className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:ring-2 focus:ring-[#0072FF]"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">Specialties</label>
                                    <div className="flex flex-wrap gap-4">
                                        {[
                                            { id: 'ocean', label: 'OCEAN', icon: Ship },
                                            { id: 'air', label: 'AIR', icon: Plane },
                                            { id: 'truck', label: 'TRUCK', icon: Truck }
                                        ].map(({ id, label, icon: Icon }) => (
                                            <label
                                                key={id}
                                                className={`flex items-center gap-3 px-4 py-3 rounded-lg border-2 cursor-pointer transition-all ${newVendor.specialties.includes(id)
                                                        ? 'border-[#0072FF] bg-[#0072FF]/10 text-[#0072FF]'
                                                        : 'border-slate-200 dark:border-slate-600 hover:border-slate-300 dark:hover:border-slate-500'
                                                    }`}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={newVendor.specialties.includes(id)}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setNewVendor({ ...newVendor, specialties: [...newVendor.specialties, id] });
                                                        } else {
                                                            setNewVendor({ ...newVendor, specialties: newVendor.specialties.filter(s => s !== id) });
                                                        }
                                                    }}
                                                    className="w-4 h-4 rounded border-slate-300 text-[#0072FF] focus:ring-[#0072FF]"
                                                />
                                                <Icon className={`w-5 h-5 ${newVendor.specialties.includes(id) ? 'text-[#0072FF]' : 'text-slate-500 dark:text-slate-400'}`} />
                                                <span className={`text-sm font-medium ${newVendor.specialties.includes(id) ? 'text-[#0072FF]' : 'text-slate-700 dark:text-slate-300'}`}>
                                                    {label}
                                                </span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Modal Footer */}
                            <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-3">
                                <button
                                    onClick={() => setShowAddVendorModal(false)}
                                    className="px-5 py-2.5 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg font-medium"
                                >
                                    Cancel
                                </button>
                                <motion.button
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    onClick={() => {
                                        if (!newVendor.name || !newVendor.email) {
                                            toast.error('Please fill in vendor name and email');
                                            return;
                                        }
                                        toast.success(`Vendor "${newVendor.name}" added successfully!`);
                                        setNewVendor({ name: '', email: '', phone: '', specialties: [] });
                                        setShowAddVendorModal(false);
                                    }}
                                    className="px-5 py-2.5 bg-gradient-to-r from-[#0072FF] to-[#00C6FB] text-white rounded-lg font-semibold flex items-center gap-2"
                                >
                                    <Plus className="w-4 h-4" />
                                    Add Vendor
                                </motion.button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default RFQ;
